/**
 * API and data access - the administrator's view of both.
 *
 * This page used to be a mock: it generated key-shaped strings in the browser,
 * kept them in localStorage alongside two invented customers, and offered a
 * "copy full key" button for each. None of it reached the server. Now that the
 * API is real, that mock is worse than nothing, because an administrator could
 * not tell the fake keys from the working ones.
 *
 * So it reads the real thing, and it is deliberately narrower than what it
 * replaced:
 *
 * - No create. Premium customers mint their own keys on /app/api-keys. An
 *   administrator handing out credentials is a worse story for everyone: it
 *   puts the secret in a second pair of hands on the way to the first.
 * - No reveal. The database holds a SHA-256 hash, not the key, so there is
 *   nothing to show. The old "copy full key" button was only possible because
 *   the values were fake.
 * - Revoke stays, because a leaked credential is an emergency somebody has to
 *   be able to stop.
 *
 * The export tab is a policy table rather than per-user switches, for the same
 * reason: allowances follow the plan and are enforced from the plan table, so
 * the way to change one is to change the customer's plan. The switches it
 * replaced saved to localStorage and were consulted by nothing.
 */

import React, { useState } from 'react';
import {
  Box, Typography, Card, IconButton, Chip, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Alert as MuiAlert, Snackbar, CircularProgress, Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { PageHeader, ConfirmDialog, EmptyState } from '../../components/common';
import { useRecipes } from '../../theme/recipes';
import { fmtDate, fmtNumber } from '../../utils/format';
import { listAllKeys, adminRevokeKey, getExportPolicy } from '../../services/adminApiAccess';

const SCOPE_LABEL = {
  'read:prices': 'Prices',
  'read:fx': 'Exchange rates',
  'read:markets': 'Markets',
  'read:commodities': 'Commodities',
};

/** "Unlimited" is a real answer, not a missing one, so it gets said. */
const rowsLabel = (n) => (n == null ? 'Unlimited' : fmtNumber(n));

function ApiConfig() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState(0);
  const [toRevoke, setToRevoke] = useState(null);
  const [toast, setToast] = useState(null);

  const keysQuery = useQuery({ queryKey: ['admin-api-keys'], queryFn: listAllKeys });
  const exportQuery = useQuery({
    queryKey: ['admin-export-policy'],
    queryFn: getExportPolicy,
    enabled: tab === 1,
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => adminRevokeKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-api-keys'] });
      setToRevoke(null);
      setToast({ severity: 'success', msg: 'Key revoked. Any call using it now fails.' });
    },
    onError: (e) => {
      setToRevoke(null);
      setToast({ severity: 'error', msg: e?.message || 'Could not revoke the key.' });
    },
  });

  const keys = keysQuery.data?.keys || [];
  const activeCount = keysQuery.data?.activeCount ?? 0;

  const loading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={26} sx={{ color: palette.accent }} />
    </Box>
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Admin"
        title="API & Data Access"
        subtitle="Every API key on the platform, and what each plan may export"
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'API & Data' }]}
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3, borderBottom: `1px solid ${palette.divider}`,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 },
          ...recipes.tabsSx,
        }}
      >
        <Tab icon={<Icon icon="ph:key" width={16} height={16} />} iconPosition="start" label="API Keys" />
        <Tab icon={<Icon icon="ph:download-simple" width={16} height={16} />} iconPosition="start" label="Data Export" />
      </Tabs>

      {/* ------------------------------------------------------ API keys */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontSize: '0.88rem', color: palette.textSecondary }}>
              <Box component="span" sx={{ fontWeight: 700, color: palette.text, fontFamily: MONO }}>
                {activeCount}
              </Box>{' '}
              active key{activeCount === 1 ? '' : 's'}
              {keys.length > activeCount && `, ${keys.length - activeCount} revoked`}
            </Typography>
          </Box>

          {keysQuery.isLoading && loading()}

          {keysQuery.error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {keysQuery.error?.message || 'Could not load the key list.'}
            </MuiAlert>
          )}

          {!keysQuery.isLoading && !keysQuery.error && keys.length === 0 && (
            <Card sx={{ p: 2.5 }}>
              <EmptyState
                compact
                variant="dashed"
                icon={<Icon icon="ph:key" width={24} height={24} />}
                title="No keys have been created"
                description="Premium customers create their own keys from the API Keys page in their workspace. They will appear here as soon as they do."
              />
            </Card>
          )}

          {!keysQuery.isLoading && !keysQuery.error && keys.length > 0 && (
            <TableContainer component={Card}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { label: 'Owner', align: 'left' },
                      { label: 'Key name', align: 'left' },
                      { label: 'Key', align: 'left' },
                      { label: 'Can read', align: 'left' },
                      { label: 'This month', align: 'right' },
                      { label: 'Last used', align: 'left' },
                      { label: 'Created', align: 'left' },
                      { label: 'Revoke', align: 'right' },
                    ].map((col) => (
                      <TableCell key={col.label} align={col.align} sx={recipes.headCellSx}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((k) => {
                    const revoked = k.status === 'revoked';
                    return (
                      <TableRow key={k.id} hover sx={{ ...recipes.bodyRowSx, opacity: revoked ? 0.55 : 1 }}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {k.ownerName || k.ownerEmail}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.15 }}>
                            <Typography sx={{ fontSize: '0.7rem', color: palette.textMuted }}>
                              {k.ownerEmail}
                            </Typography>
                            <Chip
                              label={k.ownerPlan}
                              size="small"
                              sx={{
                                height: 16, fontSize: '0.55rem', fontWeight: 700,
                                bgcolor: palette.accentBg, color: palette.accent,
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '0.85rem' }}>{k.label}</Typography>
                            {revoked && (
                              <Chip
                                label="REVOKED"
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.05em',
                                  bgcolor: palette.dangerBg, color: palette.danger,
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, fontSize: '0.75rem', color: palette.textSecondary }}>
                          <Tooltip title="Only the prefix is stored in a readable form. The key itself is a hash and cannot be shown.">
                            <Box component="span">
                              {k.prefix}
                              <Box component="span" sx={{ color: palette.textMuted }}>…</Box>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(k.scopes || []).map((s) => (
                              <Chip
                                key={s}
                                label={SCOPE_LABEL[s] || s}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.6rem', fontWeight: 600,
                                  bgcolor: palette.surface, color: palette.textSecondary,
                                  border: `1px solid ${palette.cardBorder}`,
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>
                          {fmtNumber(k.requestsThisMonth ?? 0)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: k.lastUsedAt ? palette.textSecondary : palette.textMuted }}>
                          {k.lastUsedAt ? fmtDate(k.lastUsedAt) : 'Never'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: palette.textSecondary }}>
                          {fmtDate(k.createdAt)}
                        </TableCell>
                        <TableCell align="right">
                          {!revoked && (
                            <IconButton
                              size="small"
                              aria-label={`Revoke the key named ${k.label}, owned by ${k.ownerEmail}`}
                              onClick={() => setToRevoke(k)}
                              sx={{ color: palette.textMuted, '&:hover': { color: palette.danger, bgcolor: palette.dangerBg } }}
                            >
                              <Icon icon="ph:prohibit" width={16} height={16} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mt: 2.5, px: 0.5 }}>
            <Icon icon="ph:info" width={16} height={16} color={palette.textMuted} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, lineHeight: 1.6, maxWidth: 780 }}>
              Customers create and name their own keys. You cannot create one for them, and
              no key can be read back from here: the database stores a hash, so the value
              exists only in the moment it is issued. Revoking takes effect on the next
              call and cannot be undone; the key stays listed so its usage history keeps
              its subject.
            </Typography>
          </Box>
        </Box>
      )}

      {/* --------------------------------------------------- Data export */}
      {tab === 1 && (
        <Box>
          {exportQuery.isLoading && loading()}

          {exportQuery.error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {exportQuery.error?.message || 'Could not load the export policy.'}
            </MuiAlert>
          )}

          {exportQuery.data && (
            <>
              <Typography sx={{ fontSize: '0.88rem', color: palette.textSecondary, mb: 2, maxWidth: 780, lineHeight: 1.6 }}>
                Export and API allowances follow the plan, and the server enforces these
                exact numbers. To change what one customer may do, change their plan on the{' '}
                <Box component="span" sx={{ fontWeight: 600, color: palette.text }}>User Management</Box> page.
              </Typography>

              <TableContainer component={Card} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Plan', 'Export rows a month', 'API access', 'Keys', 'Rate', 'API requests a month'].map((label, i) => (
                        <TableCell key={label} align={i === 0 ? 'left' : 'right'} sx={recipes.headCellSx}>
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exportQuery.data.plans.map((p) => (
                      <TableRow key={p.plan} hover sx={recipes.bodyRowSx}>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{p.plan}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.82rem' }}>
                          {rowsLabel(p.rowsPerMonth)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={p.apiAccess ? 'Yes' : 'No'}
                            size="small"
                            sx={{
                              height: 19, fontSize: '0.62rem', fontWeight: 700,
                              bgcolor: p.apiAccess ? palette.accentBg : palette.surface,
                              color: p.apiAccess ? palette.accent : palette.textMuted,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.82rem', color: p.apiKeys ? palette.text : palette.textMuted }}>
                          {p.apiKeys || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.82rem', color: p.apiRatePerMinute ? palette.text : palette.textMuted }}>
                          {p.apiRatePerMinute ? `${p.apiRatePerMinute}/min` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.82rem', color: p.apiRequestsPerMonth ? palette.text : palette.textMuted }}>
                          {p.apiRequestsPerMonth ? fmtNumber(p.apiRequestsPerMonth) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: palette.text, mb: 1.5 }}>
                Exports this month
              </Typography>

              {exportQuery.data.usage.length === 0 ? (
                <Card sx={{ p: 2.5 }}>
                  <EmptyState
                    compact
                    variant="dashed"
                    icon={<Icon icon="ph:download-simple" width={24} height={24} />}
                    title="Nobody has exported anything this month"
                    description="Rows exported are counted as they are sent, so this fills in as customers use the export buttons."
                  />
                </Card>
              ) : (
                <TableContainer component={Card}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Customer', 'Plan', 'Exports', 'Rows', 'Allowance'].map((label, i) => (
                          <TableCell key={label} align={i > 1 ? 'right' : 'left'} sx={recipes.headCellSx}>
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exportQuery.data.usage.map((u) => {
                        const plan = exportQuery.data.plans.find((p) => p.plan === u.plan);
                        const ceiling = plan?.rowsPerMonth;
                        const spent = ceiling != null && u.rowsThisMonth >= ceiling;
                        return (
                          <TableRow key={u.email} hover sx={recipes.bodyRowSx}>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                {u.name || u.email}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: palette.textMuted }}>{u.email}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={u.plan}
                                size="small"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: palette.accentBg, color: palette.accent }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.82rem' }}>
                              {fmtNumber(u.exportsThisMonth)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.82rem', color: spent ? palette.warning : palette.text }}>
                              {fmtNumber(u.rowsThisMonth)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.82rem', color: palette.textMuted }}>
                              {rowsLabel(ceiling)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
      )}

      <ConfirmDialog
        open={!!toRevoke}
        onClose={() => setToRevoke(null)}
        onConfirm={() => revokeMutation.mutate(toRevoke.id)}
        title="Revoke this API key?"
        description={
          toRevoke
            ? `"${toRevoke.label}", owned by ${toRevoke.ownerEmail}, stops working on its next call. Anything the customer has built on it will fail, and this cannot be undone. Tell them before you do it unless the key is known to have leaked.`
            : 'The key will stop working immediately.'
        }
        confirmLabel="Revoke key"
        tone="danger"
        icon="ph:prohibit"
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast && <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</MuiAlert>}
      </Snackbar>
    </Box>
  );
}

export default ApiConfig;
